import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, TextField, Box, Stack, IconButton, Skeleton, Typography,
  InputAdornment, Fade, LinearProgress, Select, MenuItem, FormControl,
  Tooltip, Button, Menu, Divider,
} from "@mui/material";
import {
  MdSearch, MdClose, MdRefresh, MdTune, MdArrowBack,
  MdArrowForward, MdInbox, MdMoreHoriz, MdContentCopy, MdOpenInNew,
  MdDescription, MdDownload, MdEventAvailable, MdCancel,
} from "react-icons/md";
import axios from "axios";
import { toast } from "react-toastify";
import LabOrderDetailDrawer from "./LabOrderDetailDrawer";
import { RescheduleDialog, CancelDialog, canModifyOrder } from "./LabOrderActionDialogs";
import { getProviderMeta, listProviders, getPartnerId } from "../../../utils/labProviders";

const BASE = "https://api.medstown.com";

// Report items carry a signed `tokenize_public_file_url` — that's the only
// link we surface.
const reportUrl = (r) => r?.tokenize_public_file_url || null;

// De-duped report list so the same file never appears twice.
const uniqueReports = (reports) => {
  const seen = new Set();
  return (Array.isArray(reports) ? reports : []).filter((r) => {
    const u = reportUrl(r);
    if (!u || seen.has(u)) return false;
    seen.add(u);
    return true;
  });
};

const T = {
  bg:         "#f8fafc",
  surface:    "#ffffff",
  border:     "#e5e7eb",
  borderSoft: "#f1f5f9",
  ink:        "#0f172a",
  muted:      "#64748b",
  subtle:     "#94a3b8",
  accent:     "#4f46e5",
  green:      "#059669",
  amber:      "#d97706",
  red:        "#dc2626",
  violet:     "#7c3aed",
};

const STATUS_TONE = {
  Scheduled:             T.accent,
  Confirmed:             "#0284c7",
  Assigned:              "#0891b2",
  SampleCollected:       T.violet,
  ReportGenerated:       T.green,
  Completed:             T.green,
  Rescheduled:           T.amber,
  CancellationRequested: T.amber,
  Cancelled:             T.red,
};

const STATUS_OPTIONS = [
  "Scheduled", "Confirmed", "Assigned", "SampleCollected",
  "ReportGenerated", "Completed", "Rescheduled",
  "CancellationRequested", "Cancelled",
];

const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const relativeTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  const diff = Date.now() - dt.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)    return "just now";
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  const dd = Math.floor(h / 24);
  if (dd < 30)  return `${dd}d ago`;
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// ── Primitives ──────────────────────────────────────────────────────────────
function StatusDot({ status }) {
  const tone = STATUS_TONE[status] || T.muted;
  return (
    <Stack direction="row" alignItems="center" spacing={0.7}>
      <Box sx={{
        width: 7, height: 7, borderRadius: "50%",
        bgcolor: tone,
        boxShadow: `0 0 0 3px ${tone}1a`,
      }} />
      <Typography sx={{ fontSize: "0.73rem", fontWeight: 600, color: T.ink }}>
        {status || "—"}
      </Typography>
    </Stack>
  );
}

function ProviderTag({ provider }) {
  const m = getProviderMeta(provider);
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      px: 0.9, py: 0.25, borderRadius: 1.2,
      border: `1px solid ${m.tone}33`,
      bgcolor: `${m.tone}0d`,
      color: m.tone,
      fontSize: "0.66rem",
      fontWeight: 700,
      letterSpacing: "0.02em",
    }}>
      {m.label}
    </Box>
  );
}

function PaymentTag({ status }) {
  const paid = status === "paid";
  const tone = paid ? T.green : T.amber;
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box sx={{
        width: 6, height: 6, borderRadius: "50%", bgcolor: tone,
      }} />
      <Typography sx={{
        fontSize: "0.7rem", fontWeight: 600, color: tone,
        textTransform: "capitalize",
      }}>
        {status || "—"}
      </Typography>
    </Stack>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────
export default function LabOrdersList({ onMutate }) {
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [page, setPage]             = useState(1);
  const [limit, setLimit]           = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch]         = useState("");
  const [debSearch, setDebSearch]   = useState("");
  const [status, setStatus]         = useState("");
  const [provider, setProvider]     = useState("");
  const [paymentStatus, setPayment] = useState("");
  const [collectionType, setColl]   = useState("");
  const [from, setFrom]             = useState("");
  const [to, setTo]                 = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [detailId, setDetailId]     = useState(null);
  const [menuState, setMenuState]   = useState({ anchor: null, row: null });

  // Reschedule/cancel straight from a row. The list endpoint returns whole
  // order documents (provider, status, location, rcSlotId…), so the dialogs get
  // everything they need without a second fetch — including the coordinates
  // Redcliffe's slot lookup depends on.
  const [actionOrder, setActionOrder] = useState(null);
  const [actionKind, setActionKind]   = useState(null); // "reschedule" | "cancel"

  function openAction(kind) {
    setActionOrder(menuState.row);
    setActionKind(kind);
    setMenuState({ anchor: null, row: null });
  }
  const closeAction = () => setActionKind(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ page, limit });
      if (debSearch)      p.append("q", debSearch);
      if (status)         p.append("status", status);
      if (provider)       p.append("provider", provider);
      if (paymentStatus)  p.append("paymentStatus", paymentStatus);
      if (collectionType) p.append("collectionType", collectionType);
      if (from)           p.append("from", new Date(from).toISOString());
      if (to) {
        const end = new Date(to);
        end.setHours(23, 59, 59, 999);
        p.append("to", end.toISOString());
      }
      const { data } = await axios.get(`${BASE}/admin/lab/orders?${p}`);
      setRows(data.orders || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalCount(data.pagination?.total || 0);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load orders");
    } finally { setLoading(false); }
  }, [page, limit, debSearch, status, provider, paymentStatus, collectionType, from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeCount = useMemo(() => {
    return [status, provider, paymentStatus, collectionType, from, to].filter(Boolean).length;
  }, [status, provider, paymentStatus, collectionType, from, to]);

  function clearFilters() {
    setStatus(""); setProvider(""); setPayment("");
    setColl(""); setFrom(""); setTo(""); setPage(1);
  }

  function copy(e, text, label) {
    e.stopPropagation();
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  }

  function handleMutated() {
    fetchData();
    onMutate?.();
  }

  const from0 = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const to0   = Math.min(page * limit, totalCount);

  // Reusable select sx
  const selectSx = {
    height: 34,
    fontSize: "0.78rem",
    bgcolor: T.surface,
    "& fieldset": { borderColor: T.border },
    "&:hover fieldset": { borderColor: T.subtle },
  };

  return (
    <Card elevation={0} sx={{
      bgcolor: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: 3,
      overflow: "hidden",
      position: "relative",
    }}>
      {loading && (
        <LinearProgress sx={{
          position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, height: 2,
          bgcolor: "transparent",
          "& .MuiLinearProgress-bar": { bgcolor: T.accent },
        }} />
      )}

      {/* Toolbar */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${T.borderSoft}` }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small" placeholder="Search booking, partner ID, phone, pincode…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{
              flex: 1, maxWidth: 420,
              "& .MuiOutlinedInput-root": {
                height: 34, fontSize: "0.8rem",
                bgcolor: T.surface,
                "& fieldset": { borderColor: T.border },
                "&:hover fieldset": { borderColor: T.subtle },
                "&.Mui-focused fieldset": { borderColor: T.accent, borderWidth: 1 },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch size={16} color={T.muted} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.3 }}>
                    <MdClose size={14} color={T.muted} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <Button
            size="small"
            onClick={() => setFiltersOpen((v) => !v)}
            startIcon={<MdTune size={15} />}
            sx={{
              height: 34, px: 1.4, textTransform: "none", fontSize: "0.78rem", fontWeight: 600,
              color: filtersOpen || activeCount ? T.accent : T.muted,
              bgcolor: filtersOpen || activeCount ? `${T.accent}0d` : "transparent",
              border: `1px solid ${filtersOpen || activeCount ? `${T.accent}40` : T.border}`,
              borderRadius: 1.5,
              "&:hover": { bgcolor: `${T.accent}0d`, borderColor: `${T.accent}40` },
            }}
          >
            Filters
            {activeCount > 0 && (
              <Box component="span" sx={{
                ml: 0.6, px: 0.6, borderRadius: 1, bgcolor: T.accent, color: "#fff",
                fontSize: "0.62rem", fontWeight: 700,
              }}>
                {activeCount}
              </Box>
            )}
          </Button>

          {activeCount > 0 && (
            <Button
              size="small" onClick={clearFilters}
              sx={{
                height: 34, textTransform: "none", fontSize: "0.76rem",
                color: T.muted, "&:hover": { color: T.red, bgcolor: "transparent" },
              }}
            >
              Clear
            </Button>
          )}

          <Box sx={{ flex: 1 }} />

          <Typography sx={{ fontSize: "0.72rem", color: T.muted, fontVariantNumeric: "tabular-nums" }}>
            {from0}–{to0} of {totalCount.toLocaleString("en-IN")}
          </Typography>

          <Tooltip title="Refresh" arrow>
            <IconButton size="small" onClick={fetchData}
              sx={{
                border: `1px solid ${T.border}`, borderRadius: 1.2,
                color: T.muted, width: 34, height: 34,
                "&:hover": { color: T.accent, borderColor: `${T.accent}40`, bgcolor: `${T.accent}0d` },
              }}>
              <MdRefresh size={15} />
            </IconButton>
          </Tooltip>
        </Stack>

        {filtersOpen && (
          <Fade in>
            <Stack direction="row" spacing={1} sx={{ mt: 1.3, flexWrap: "wrap", gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  displayEmpty value={status}
                  onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                  sx={selectSx}
                  renderValue={(v) => v || <span style={{ color: T.subtle }}>Status</span>}
                >
                  <MenuItem value="">Any status</MenuItem>
                  {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  displayEmpty value={provider}
                  onChange={(e) => { setProvider(e.target.value); setPage(1); }}
                  sx={selectSx}
                  renderValue={(v) => v ? getProviderMeta(v).label : <span style={{ color: T.subtle }}>Provider</span>}
                >
                  <MenuItem value="">Any provider</MenuItem>
                  {listProviders().map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  displayEmpty value={paymentStatus}
                  onChange={(e) => { setPayment(e.target.value); setPage(1); }}
                  sx={selectSx}
                  renderValue={(v) => v ? (v[0].toUpperCase() + v.slice(1)) : <span style={{ color: T.subtle }}>Payment</span>}
                >
                  <MenuItem value="">Any payment</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  displayEmpty value={collectionType}
                  onChange={(e) => { setColl(e.target.value); setPage(1); }}
                  sx={selectSx}
                  renderValue={(v) => v ? (v[0].toUpperCase() + v.slice(1)) : <span style={{ color: T.subtle }}>Collection</span>}
                >
                  <MenuItem value="">Any collection</MenuItem>
                  <MenuItem value="home">Home</MenuItem>
                  <MenuItem value="lab">Lab visit</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small" type="date" value={from}
                onChange={(e) => { setFrom(e.target.value); setPage(1); }}
                sx={{ width: 150, "& .MuiOutlinedInput-root": { height: 34, fontSize: "0.76rem", bgcolor: T.surface, "& fieldset": { borderColor: T.border } } }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: "0.68rem", color: T.muted, mr: 0.5 }}>From</Typography></InputAdornment>,
                }}
              />
              <TextField
                size="small" type="date" value={to}
                onChange={(e) => { setTo(e.target.value); setPage(1); }}
                sx={{ width: 150, "& .MuiOutlinedInput-root": { height: 34, fontSize: "0.76rem", bgcolor: T.surface, "& fieldset": { borderColor: T.border } } }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: "0.68rem", color: T.muted, mr: 0.5 }}>To</Typography></InputAdornment>,
                }}
              />
            </Stack>
          </Fade>
        )}
      </Box>

      {error && (
        <Box sx={{
          mx: 1.5, mt: 1.2, px: 1.4, py: 0.8,
          borderRadius: 1.5,
          bgcolor: `${T.red}0d`,
          border: `1px solid ${T.red}33`,
        }}>
          <Typography sx={{ fontSize: "0.73rem", color: T.red, fontWeight: 600 }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* Table */}
      <TableContainer>
        <Table sx={{
          "& .MuiTableCell-root": {
            py: 1.4, px: 1.8,
            fontSize: "0.78rem",
            borderBottom: `1px solid ${T.borderSoft}`,
          },
          "& .MuiTableRow-root:last-child .MuiTableCell-root": { borderBottom: 0 },
        }}>
          <TableHead>
            <TableRow sx={{
              "& .MuiTableCell-head": {
                bgcolor: "transparent",
                color: T.muted,
                fontWeight: 600,
                fontSize: "0.62rem",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                py: 1,
                borderBottom: `1px solid ${T.border}`,
              },
            }}>
              <TableCell>Booking</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Tests</TableCell>
              <TableCell>Scheduled</TableCell>
              <TableCell>Partner</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !rows.length ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 9 }).map((_, j) => (
                    <TableCell key={j}><Skeleton height={20} sx={{ borderRadius: 1 }} /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 8, borderBottom: 0 }}>
                  <Stack alignItems="center" spacing={1}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: "50%",
                      bgcolor: T.borderSoft, color: T.subtle,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <MdInbox size={22} />
                    </Box>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: T.ink }}>
                      No lab orders
                    </Typography>
                    <Typography sx={{ fontSize: "0.74rem", color: T.muted }}>
                      {activeCount || search ? "Try adjusting your filters" : "Orders will appear here once booked"}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : rows.map((row) => {
              const firstPatient = row.patients?.[0];
              const itemCount    = row.cartItems?.length || 0;
              const firstItem    = row.cartItems?.[0]?.name;
              const partnerId    = getPartnerId(row);
              const reps         = uniqueReports(row.reports);

              return (
                <TableRow
                  key={row._id}
                  hover
                  sx={{
                    cursor: "pointer",
                    transition: "background 120ms ease",
                    "&:hover": { bgcolor: "#fafbfc" },
                  }}
                  onClick={() => setDetailId(row._id)}
                >
                  <TableCell>
                    <Typography sx={{
                      fontSize: "0.78rem", fontWeight: 700, color: T.ink,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      lineHeight: 1.2,
                    }}>
                      {row.medstownBookingId || row._id?.slice(-8)}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.4} sx={{ mt: 0.2 }}>
                      {partnerId && (
                        <Typography sx={{
                          fontSize: "0.66rem", color: T.subtle,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}>
                          {partnerId}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: "0.64rem", color: T.subtle }}>
                        · {relativeTime(row.createdAt)}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.1}>
                     
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: T.ink, lineHeight: 1.2 }} noWrap>
                          {firstPatient?.name || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.68rem", color: T.muted, fontVariantNumeric: "tabular-nums" }} noWrap>
                          {firstPatient?.phone || "—"}
                          {row.patients?.length > 1 && (
                            <Box component="span" sx={{ color: T.accent, fontWeight: 600 }}>
                              {` +${row.patients.length - 1}`}
                            </Box>
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Typography
                      sx={{ fontSize: "0.78rem", fontWeight: 500, color: T.ink, maxWidth: 190, lineHeight: 1.2 }}
                      noWrap
                      title={row.cartItems?.map((i) => i.name).join(", ")}
                    >
                      {firstItem || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.66rem", color: T.muted, mt: 0.2 }}>
                      {itemCount} item{itemCount !== 1 ? "s" : ""} · {row.collectionType === "lab" ? "Lab visit" : "Home collection"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography sx={{ fontSize: "0.76rem", fontWeight: 600, color: T.ink, lineHeight: 1.2 }}>
                      {row.scheduledDate || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.66rem", color: T.muted, fontVariantNumeric: "tabular-nums" }}>
                      {row.scheduledSlot || "—"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <ProviderTag provider={row.provider} />
                  </TableCell>

                  <TableCell align="right">
                    <Typography sx={{
                      fontSize: "0.86rem", fontWeight: 700, color: T.ink,
                      fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em",
                    }}>
                      {inr(row.total)}
                    </Typography>
                    {row.discountAmount > 0 && (
                      <Typography sx={{ fontSize: "0.64rem", color: T.green, fontVariantNumeric: "tabular-nums" }}>
                        −{inr(row.discountAmount)}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <PaymentTag status={row.paymentStatus} />
                  </TableCell>

                  <TableCell>
                    <Stack spacing={0.5}>
                      <StatusDot status={row.status} />
                      {reps.length > 0 ? (
                        <Stack
                          direction="row" alignItems="center" spacing={0.4}
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = reportUrl(reps[0]);
                            if (url) window.open(url, "_blank", "noopener,noreferrer");
                          }}
                          sx={{
                            alignSelf: "flex-start", cursor: "pointer",
                            px: 0.7, py: 0.2, borderRadius: 1,
                            bgcolor: `${T.green}0d`, border: `1px solid ${T.green}33`,
                            "&:hover": { bgcolor: `${T.green}14` },
                          }}
                          title="Open report"
                        >
                          <MdDescription size={11} color={T.green} />
                          <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: T.green }}>
                            Report{reps.length > 1 ? ` ×${reps.length}` : ""}
                          </Typography>
                          <MdDownload size={11} color={T.green} />
                        </Stack>
                      ) : (
                        <Typography sx={{ fontSize: "0.6rem", color: T.subtle }}>
                          No report yet
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={(e) => setMenuState({ anchor: e.currentTarget, row })}
                      sx={{
                        color: T.subtle, p: 0.5,
                        "&:hover": { color: T.ink, bgcolor: T.borderSoft },
                      }}
                    >
                      <MdMoreHoriz size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer / pagination */}
      <Stack
        direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 1.8, py: 1.2, borderTop: `1px solid ${T.borderSoft}` }}
      >
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <Typography sx={{ fontSize: "0.72rem", color: T.muted }}>Rows</Typography>
          <Select
            size="small" value={limit}
            onChange={(e) => { setLimit(e.target.value); setPage(1); }}
            sx={{
              height: 28, fontSize: "0.72rem",
              "& fieldset": { borderColor: T.border },
            }}
          >
            {[10, 20, 50, 100].map((n) => <MenuItem key={n} value={n} dense>{n}</MenuItem>)}
          </Select>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton
            size="small" disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            sx={{
              color: T.muted, border: `1px solid ${T.border}`, borderRadius: 1.2,
              width: 28, height: 28,
              "&:hover": { bgcolor: T.borderSoft },
              "&.Mui-disabled": { borderColor: T.borderSoft, color: T.border },
            }}
          >
            <MdArrowBack size={13} />
          </IconButton>
          <Typography sx={{
            fontSize: "0.74rem", color: T.ink, fontWeight: 600,
            px: 1.2, fontVariantNumeric: "tabular-nums",
          }}>
            {page} <Box component="span" sx={{ color: T.subtle, fontWeight: 400 }}>/ {totalPages}</Box>
          </Typography>
          <IconButton
            size="small" disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            sx={{
              color: T.muted, border: `1px solid ${T.border}`, borderRadius: 1.2,
              width: 28, height: 28,
              "&:hover": { bgcolor: T.borderSoft },
              "&.Mui-disabled": { borderColor: T.borderSoft, color: T.border },
            }}
          >
            <MdArrowForward size={13} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Row action menu */}
      <Menu
        anchorEl={menuState.anchor}
        open={!!menuState.anchor}
        onClose={() => setMenuState({ anchor: null, row: null })}
        PaperProps={{
          sx: {
            mt: 0.5, borderRadius: 2, minWidth: 200,
            border: `1px solid ${T.border}`,
            boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
          },
        }}
      >
        <MenuItem
          dense
          onClick={() => { setDetailId(menuState.row?._id); setMenuState({ anchor: null, row: null }); }}
          sx={{ fontSize: "0.78rem", gap: 1 }}
        >
          <MdOpenInNew size={14} /> Open details
        </MenuItem>
        <MenuItem
          dense
          onClick={(e) => { copy(e, menuState.row?.medstownBookingId, "Booking ID"); setMenuState({ anchor: null, row: null }); }}
          sx={{ fontSize: "0.78rem", gap: 1 }}
        >
          <MdContentCopy size={14} /> Copy booking ID
        </MenuItem>
        {getPartnerId(menuState.row) && (
          <MenuItem
            dense
            onClick={(e) => {
              const label = `${getProviderMeta(menuState.row.provider).idLabel} ID`;
              copy(e, getPartnerId(menuState.row), label);
              setMenuState({ anchor: null, row: null });
            }}
            sx={{ fontSize: "0.78rem", gap: 1 }}
          >
            <MdContentCopy size={14} /> Copy {getProviderMeta(menuState.row?.provider).idLabel} ID
          </MenuItem>
        )}
        {menuState.row?.patients?.[0]?.phone && (
          <MenuItem
            dense
            onClick={(e) => { copy(e, menuState.row.patients[0].phone, "Phone"); setMenuState({ anchor: null, row: null }); }}
            sx={{ fontSize: "0.78rem", gap: 1 }}
          >
            <MdContentCopy size={14} /> Copy phone
          </MenuItem>
        )}
        {uniqueReports(menuState.row?.reports).length > 0 && (
          <MenuItem
            dense
            onClick={(e) => {
              e.stopPropagation();
              const url = reportUrl(uniqueReports(menuState.row.reports)[0]);
              if (url) window.open(url, "_blank", "noopener,noreferrer");
              setMenuState({ anchor: null, row: null });
            }}
            sx={{ fontSize: "0.78rem", gap: 1, color: T.green }}
          >
            <MdDownload size={14} /> Open report{uniqueReports(menuState.row.reports).length > 1 ? `s (${uniqueReports(menuState.row.reports).length})` : ""}
          </MenuItem>
        )}
        {uniqueReports(menuState.row?.reports).length > 0 && (
          <MenuItem
            dense
            onClick={async (e) => {
              e.stopPropagation();
              const url = reportUrl(uniqueReports(menuState.row.reports)[0]);
              setMenuState({ anchor: null, row: null });
              if (!url) return;
              if (navigator.share) {
                try { await navigator.share({ title: `Lab report · ${menuState.row.medstownBookingId || ""}`, url }); return; }
                catch (err) { if (err?.name === "AbortError") return; }
              }
              try { await navigator.clipboard.writeText(url); toast.success("Report link copied"); }
              catch { toast.error("Couldn't share report link"); }
            }}
            sx={{ fontSize: "0.78rem", gap: 1 }}
          >
            <MdContentCopy size={14} /> Share report link
          </MenuItem>
        )}

        {/* Partner actions. Hidden outright on a cancelled/completed booking —
            no lab accepts a change at that point, so offering it would only
            produce a guaranteed rejection. Both providers are supported: the
            dialogs read their partner-specific rules from labProviders. */}
        {canModifyOrder(menuState.row) && <Divider sx={{ my: 0.5 }} />}
        {canModifyOrder(menuState.row) && (
          <MenuItem
            dense
            onClick={() => openAction("reschedule")}
            sx={{ fontSize: "0.78rem", gap: 1 }}
          >
            <MdEventAvailable size={14} /> Reschedule
          </MenuItem>
        )}
        {canModifyOrder(menuState.row) && (
          <MenuItem
            dense
            onClick={() => openAction("cancel")}
            sx={{ fontSize: "0.78rem", gap: 1, color: T.red }}
          >
            <MdCancel size={14} /> Cancel order
          </MenuItem>
        )}
      </Menu>

      <RescheduleDialog
        open={actionKind === "reschedule"}
        onClose={closeAction}
        order={actionOrder}
        onDone={handleMutated}
      />
      <CancelDialog
        open={actionKind === "cancel"}
        onClose={closeAction}
        order={actionOrder}
        onDone={handleMutated}
      />

      <LabOrderDetailDrawer
        orderId={detailId}
        onClose={() => setDetailId(null)}
        onMutated={handleMutated}
      />
    </Card>
  );
}
